import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { AiInstructionDataTableItem } from '@kirin/api-client/contracts/ai-instructions';
import { queryKeys } from '@/hooks/api/queryKeys';
import { createAiInstructionColumns } from './columns';
import { fetchAiInstructions } from './api';
import { AI_INSTRUCTION_TABLE_CONFIG } from './constants';
import { InstructionDetailModal } from '@/pages/organization/components/InstructionDetailModal';
import { InstructionUserListModal } from '@/pages/organization/components/InstructionUserListModal';
import { InstructionProjectListModal } from '@/pages/organization/components/InstructionProjectListModal';

export function DataTableAiInstructionsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  // Current debounced search term — shared with modals for text highlighting.
  const [modalSearchTerm, setModalSearchTerm] = useState('');

  // Modal state - hoisted to parent to avoid mounting modal per row
  const [selectedDetailContentId, setSelectedDetailContentId] = useState<
    string | null
  >(null);
  // Users + Projects modals are keyed by (contentId, fileCategory) — the
  // DataTable groups by both, so each modal must carry the clicked row's
  // category to match its cell/count (KIR-3356). null category = the untyped row.
  const [selectedUsers, setSelectedUsers] = useState<{
    contentId: string;
    fileCategory: string | null;
  } | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<{
    contentId: string;
    fileCategory: string | null;
  } | null>(null);

  // Handlers for cell click events
  const handleNameClick = useCallback((contentId: string) => {
    setSelectedDetailContentId(contentId);
  }, []);

  const handleUsersClick = useCallback(
    (contentId: string, fileCategory: string | null) => {
      setSelectedUsers({ contentId, fileCategory });
    },
    []
  );

  const handleProjectsClick = useCallback(
    (contentId: string, fileCategory: string | null) => {
      setSelectedProjects({ contentId, fileCategory });
    },
    []
  );

  const handleCloseDetailModal = useCallback(() => {
    setSelectedDetailContentId(null);
  }, []);

  const handleCloseUsersModal = useCallback(() => {
    setSelectedUsers(null);
  }, []);

  const handleCloseProjectsModal = useCallback(() => {
    setSelectedProjects(null);
  }, []);

  const columns = useMemo(
    () =>
      createAiInstructionColumns(organizationId, {
        onNameClick: handleNameClick,
        onProjectsClick: handleProjectsClick,
        onUsersClick: handleUsersClick,
      }),
    [organizationId, handleNameClick, handleProjectsClick, handleUsersClick]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) => {
      return queryKeys.aiInstructions.dataTable(organizationId, {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      });
    },
    [organizationId]
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Review the instructions configured in your organization (Rules,
          Skills, and Commands). If you use Kirin as a single user, this list
          reflects your workspace. Please note: Some items may not appear here
          due to access or synchronization limitations (for example, files that
          aren&apos;t available locally or haven&apos;t been synced yet).
        </AlertDescription>
      </Alert>
      <DataTable<AiInstructionDataTableItem>
        id={AI_INSTRUCTION_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the AI instructions table"
        search={{
          placeholder: 'Search instructions...',
          className: 'w-[280px]',
          onSearch: setModalSearchTerm,
        }}
        features={{ enableSorting: true }}
        queryFn={(params, signal) =>
          fetchAiInstructions(organizationId, params, signal)
        }
        getQueryKeysFromParams={getQueryKeysFromParams}
        noDataLabel="No AI instructions found for this organization."
        countScope={{ organizationId }}
        queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
      />
      <InstructionDetailModal
        organizationId={organizationId}
        contentId={selectedDetailContentId}
        onClose={handleCloseDetailModal}
        searchTerm={modalSearchTerm}
      />
      <InstructionUserListModal
        organizationId={organizationId}
        contentId={selectedUsers?.contentId ?? null}
        fileCategory={selectedUsers?.fileCategory}
        onClose={handleCloseUsersModal}
      />
      <InstructionProjectListModal
        organizationId={organizationId}
        contentId={selectedProjects?.contentId ?? null}
        fileCategory={selectedProjects?.fileCategory}
        onClose={handleCloseProjectsModal}
      />
    </div>
  );
}

export default function DataTableAiInstructionsTab({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  return <DataTableAiInstructionsContent organizationId={organizationId} />;
}
